import { useEffect, useState } from 'react';
import { fetchBreakoutAlert, type BreakoutAlertDTO, type AlertSignal } from '../api';

interface BreakoutAlertWidgetProps {
    symbol: string;
}

export const BreakoutAlertWidget = ({ symbol }: BreakoutAlertWidgetProps) => {
    const [alert, setAlert] = useState<BreakoutAlertDTO | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isCancelled = false;
        setIsLoading(true);

        fetchBreakoutAlert(symbol).then(data => {
            if (!isCancelled) {
                setAlert(data);
                setIsLoading(false);
            }
        });

        return () => { isCancelled = true; };
    }, [symbol]);

    // 1. Sleek Loading State
    if (isLoading) {
        return (
            <div className="h-32 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 animate-pulse flex items-center justify-center">
                <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                    <div className="w-5 h-5 border-2 border-slate-300 dark:border-slate-600 border-t-blue-500 rounded-full animate-spin"></div>
                    <span className="text-sm font-medium tracking-wide uppercase">Computing Institutional Momentum...</span>
                </div>
            </div>
        );
    }

    // 2. Fail silently if no data is available (keeps UI clean and uncluttered)
    if (!alert || alert.signal === 'INSUFFICIENT_DATA') {
        return null;
    }

    // --- 3. Dynamic Theming Engine ---
    const getTheme = (signal: AlertSignal) => {
        switch (signal) {
            case 'STRONG_BUY': return {
                bg: 'bg-emerald-50 dark:bg-emerald-900/10',
                border: 'border-emerald-500 dark:border-emerald-500/50',
                text: 'text-emerald-700 dark:text-emerald-400',
                label: 'STRONG BUY (BREAKOUT)',
                icon: '🚀',
                barColor: 'bg-emerald-500'
            };
            case 'BUY_WATCH': return {
                bg: 'bg-blue-50 dark:bg-blue-900/10',
                border: 'border-blue-500 dark:border-blue-500/50',
                text: 'text-blue-700 dark:text-blue-400',
                label: 'BUY WATCH (SETUP)',
                icon: '👀',
                barColor: 'bg-blue-500'
            };
            case 'STRONG_SELL': return {
                bg: 'bg-rose-50 dark:bg-rose-900/10',
                border: 'border-rose-500 dark:border-rose-500/50',
                text: 'text-rose-700 dark:text-rose-400',
                label: 'STRONG SELL (BREAKDOWN)',
                icon: '⚠️',
                barColor: 'bg-rose-500'
            };
            case 'SELL_WATCH': return {
                bg: 'bg-orange-50 dark:bg-orange-900/10',
                border: 'border-orange-500 dark:border-orange-500/50',
                text: 'text-orange-700 dark:text-orange-400',
                label: 'SELL WATCH',
                icon: '📉',
                barColor: 'bg-orange-500'
            };
            default: return {
                bg: 'bg-slate-50 dark:bg-slate-800/40',
                border: 'border-slate-300 dark:border-slate-600',
                text: 'text-slate-700 dark:text-slate-300',
                label: 'HOLD (CONSOLIDATING)',
                icon: '⚖️',
                barColor: 'bg-slate-400 dark:bg-slate-500'
            };
        }
    };

    const theme = getTheme(alert.signal);

    // Math Helpers
    // Currency-agnostic: emit the bare number (the listing currency is already
    // shown elsewhere on the Info Page), keeping this slider universal.
    const formatValue = (val?: number | null) => val != null ? val.toFixed(2) : 'N/A';
    const isMacroUptrend = alert.sma_50 != null && alert.sma_200 != null ? alert.sma_50 > alert.sma_200 : null;

    // Calculate Donchian Channel Position
    // This maps the current close price to a % between the 20-day Support and Resistance.
    const calculateRangePercentage = () => {
        if (alert.prev_20d_high == null || alert.prev_20d_low == null) return 50;
        const range = alert.prev_20d_high - alert.prev_20d_low;
        if (range <= 0) return 50;
        const position = ((alert.close_price - alert.prev_20d_low) / range) * 100;
        // We clamp it slightly outside 0-100% so if it breaks out, the marker visibly pushes past the channel!
        return Math.max(-5, Math.min(105, position));
    };

    return (
        <div className={`w-full rounded-xl border-l-4 border-y border-r ${theme.border} ${theme.bg} shadow-sm overflow-hidden relative transition-all duration-300`}>

            {/* Timestamp Badge */}
            <div className="absolute top-0 right-0 px-3 py-1 bg-black/5 dark:bg-white/5 rounded-bl-lg text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400">
                Calculated: {new Date(alert.updated_at).toLocaleDateString()}
            </div>

            <div className="p-5 sm:p-6 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6 mt-2 xl:mt-0">

                {/* 1. Main Signal Section (The "What") */}
                <div className="flex-1">
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                        Algorithmic Momentum
                    </p>
                    <div className="flex items-center gap-3">
                        <span className="text-2xl sm:text-3xl filter drop-shadow-sm">{theme.icon}</span>
                        <h2 className={`text-xl sm:text-2xl font-black tracking-tight ${theme.text}`}>
                            {theme.label}
                        </h2>
                    </div>
                    <p className="text-sm mt-2 text-slate-600 dark:text-slate-300 max-w-xl leading-relaxed font-medium">
                        {alert.signal === 'STRONG_BUY' && `Exceptional setup. ${symbol} has broken through 20-day resistance on massive volume conviction while holding a macro uptrend.`}
                        {alert.signal === 'BUY_WATCH' && `Price is testing 20-day resistance levels, but lacks the extreme volume or macro trend support required for a full breakout confirmation.`}
                        {alert.signal === 'HOLD_CONSOLIDATING' && `${symbol} is chopping sideways within its 20-day trading range. No actionable momentum edge.`}
                        {alert.signal === 'STRONG_SELL' && `Severe technical breakdown. ${symbol} has crashed through 20-day support on high volume in a macro downtrend.`}
                        {alert.signal === 'SELL_WATCH' && `Price is testing 20-day support levels, showing weakness but lacking heavy distribution volume.`}
                    </p>
                </div>

                {/* 2. Donchian Channel Visualizer (The "Where") */}
                <div className="flex-1 w-full xl:max-w-md bg-white/60 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700 shadow-inner">
                    <div className="flex justify-between text-[11px] font-mono font-bold text-slate-500 dark:text-slate-400 mb-2 uppercase">
                        <span>Support: {formatValue(alert.prev_20d_low)}</span>
                        <span>Resistance: {formatValue(alert.prev_20d_high)}</span>
                    </div>

                    {/* The Progress Bar */}
                    <div className="h-2.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full relative overflow-visible">
                        <div
                            className={`absolute top-0 left-0 h-full rounded-full transition-all duration-1000 ${theme.barColor}`}
                            style={{ width: `${Math.max(0, Math.min(100, calculateRangePercentage()))}%` }}
                        />
                        {/* A marker showing exactly where the current price is */}
                        <div
                            className="absolute top-1/2 -translate-y-1/2 h-4 w-1.5 bg-slate-900 dark:bg-white rounded-full shadow border border-white dark:border-slate-900"
                            style={{ left: `calc(${calculateRangePercentage()}% - 3px)` }}
                        />
                    </div>
                    <div className="text-center mt-3 text-sm font-bold text-slate-900 dark:text-white flex items-center justify-center gap-2">
                        <span>Current Close:</span>
                        <span className={`font-mono px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 ${
                            alert.signal.includes('BUY') ? 'text-emerald-600 dark:text-emerald-400' :
                            alert.signal.includes('SELL') ? 'text-rose-600 dark:text-rose-400' : ''
                        }`}>
                            {formatValue(alert.close_price)}
                        </span>
                    </div>
                </div>

                {/* 3. Conviction Metrics Grid (The "Why") */}
                <div className="grid grid-cols-2 xl:flex xl:flex-col gap-3 flex-shrink-0 w-full xl:w-56">

                    <div className="bg-white/60 dark:bg-slate-900/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700 flex flex-col justify-center">
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Volume Conviction</p>
                        <p className={`text-lg font-mono font-bold mt-0.5 ${
                            (alert.volume_surge_multiplier || 0) >= 1.5 ? 'text-purple-600 dark:text-purple-400' : 'text-slate-700 dark:text-slate-300'
                        }`}>
                            {alert.volume_surge_multiplier ? `${alert.volume_surge_multiplier.toFixed(2)}x` : 'N/A'}
                        </p>
                    </div>

                    <div className="bg-white/60 dark:bg-slate-900/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700 flex flex-col justify-center">
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Macro Trend</p>
                        <p className={`text-lg font-bold mt-0.5 ${
                            isMacroUptrend === true ? 'text-emerald-600 dark:text-emerald-400' :
                            isMacroUptrend === false ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'
                        }`}>
                            {isMacroUptrend === true ? 'BULLISH' : isMacroUptrend === false ? 'BEARISH' : 'N/A'}
                        </p>
                    </div>

                    {/* NEW: Intraday Close (Candle Conviction) */}
                    <div className="bg-white/60 dark:bg-slate-900/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700 flex flex-col justify-center">
                        <div className="flex justify-between items-center mb-1.5">
                            <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Candle Close</p>
                            <span className="text-[10px] font-mono font-bold text-slate-400">
                                {alert.close_position != null ? `${(alert.close_position * 100).toFixed(0)}%` : 'N/A'}
                            </span>
                        </div>
                        {alert.close_position != null ? (
                            <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full relative shadow-inner overflow-hidden">
                                <div
                                    className={`absolute top-0 bottom-0 left-0 transition-all duration-1000 rounded-full ${
                                        alert.close_position >= 0.70 ? 'bg-emerald-500' :
                                        alert.close_position <= 0.30 ? 'bg-rose-500' : 'bg-amber-400'
                                    }`}
                                    style={{ width: `${alert.close_position * 100}%` }}
                                />
                            </div>
                        ) : (
                            <p className="text-sm font-bold text-slate-400">N/A</p>
                        )}
                    </div>

                    {/* NEW: ATR (Volatility) */}
                    <div className="bg-white/60 dark:bg-slate-900/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700 flex flex-col justify-center">
                        <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Volatility (ATR)</p>
                        <p className="text-lg font-mono font-bold mt-0.5 text-slate-700 dark:text-slate-300">
                            {alert.atr_20 != null ? `${alert.atr_20.toFixed(2)}` : 'N/A'}
                        </p>
                    </div>

                </div>

            </div>
        </div>
    );
};
